import type {
  AddNewBrandUserRequest,
  BrandPlanTrialDates,
  BrandTagDisplay,
  BusinessHoursResponse,
  CreateApiKeyRequest,
  DHProductSubscription,
  FacebookAnalyticsAvailableAccount,
  FacebookAvailableAccount,
  GetApiBrandsBrandIdUsersParams,
  GetApiBrandsParams,
  GetApiOrganizationsOrganizationIdDhProductSubscriptionsParams,
  GetApiUsersParams,
  GetCompanyCustomerInfo,
  HubspotVisitorTokenResponse,
  IntegrationDetails,
  LinkedInAvailableAccountsResponse,
  ListApiKeysResponse,
  ListBrandUsers,
  SelfIntegrationsResponse,
  SelfOAuthAuthorization,
  SnapchatAvailableAccountsResponse,
  StripeCheckoutSession,
  SubscriptionSourceData,
  UpdateBrandUserRequest,
  UpsertBusinessHoursRequest,
  UserExpanded as UserExpandedCamel,
} from '@dashhudson/api-client/auth/model';
import type {
  ActionMetadata as GeneratedActionMetadata,
  BrandCompact,
  BrandCompactWithTrialPermissions,
  BrandExpanded,
  BrandExpandedWithTrialPermissions,
  BrandUser,
  BrandUserExpanded,
  CreateApiKeyResponse,
  CreatePasswordResetResponse,
  PasswordResetsResponse,
  PublicPlatformConnection,
  TaskResponse,
  Token,
  UserCompact,
  UserExpanded,
} from '@dashhudson/api-client/auth/model/raw';

import {
  getDeleteApiBrandsBrandIdApiKeysApiKeyIdUrl,
  getDeleteApiBrandsBrandIdUsersUserIdUrl,
  getDeleteApiOrganizationsOrganizationIdBrandTagsBrandTagIdUrl,
  getDeleteApiOrganizationsOrganizationIdBrandsBrandIdTagsBrandTagIdUrl,
  getDeleteApiPlatformConnectionsConnectionIdUrl,
  getDeleteApiSelfOauthAuthorizationsAuthorizationIdUrl,
  getDeleteApiTokensSelfUrl,
  getGetApiBrandsBrandIdApiKeysUrl,
  getGetApiBrandsBrandIdIntegrationsUrl,
  getGetApiBrandsBrandIdUsersUrl,
  getGetApiBrandsBrandIdUsersUserIdUrl,
  getGetApiBrandsUserUserIdExportsUserListUrl,
  getGetApiCompaniesCompanyIdCustomerInformationUrl,
  getGetApiOrganizationsOrganizationIdBrandTagsUrl,
  getGetApiOrganizationsOrganizationIdDhProductSubscriptionsUrl,
  getGetApiPasswordResetsUserHashUrl,
  getGetApiSelfActionMetadataUrl,
  getGetApiSelfIntegrationsUrl,
  getGetApiSelfOauthAuthorizationsUrl,
  getGetApiSelfSubscriptionsUrl,
  getGetApiSelfUrl,
  getGetApiUsersUrl,
  getGetApiUsersUserIdUrl,
  getPatchApiBrandsBrandIdActionMetadataActionContextUrl,
  getPatchApiBrandsBrandIdUsersUserIdUrl,
  getPatchApiUsersUserIdUrl,
  getPostApiBillingCheckoutSessionsUrl,
  getPostApiBrandsBrandIdApiKeysUrl,
  getPostApiBrandsBrandIdTrialUrl,
  getPostApiBrandsBrandIdUsersUrl,
  getPostApiBrandsBrandIdUsersUserIdResendInvitationUrl,
  getPostApiBusinessHoursUrl,
  getPostApiDhProductSubscriptionsStartTrialUrl,
  getPostApiOrganizationsOrganizationIdBrandAssociationsUrl,
  getPostApiOrganizationsOrganizationIdBrandTagsUrl,
  getPostApiOrganizationsOrganizationIdSubscriptionItemsUrl,
  getPostApiPasswordResetsUrl,
  getPostApiProviderOauthUrl,
  getPostApiSamlAssertionsUrl,
  getPostApiSelfHubspotVisitorTokenUrl,
  getPostApiTokensUrl,
  getPostApiUsersUserIdChangePasswordUrl,
  getPostApiUsersUserIdResetPasswordUrl,
  getPutApiBusinessHoursUrl,
} from '@dashhudson/api-client/auth/client';
import merge from 'lodash/merge';
import snakeCase from 'lodash/snakeCase';

import type { ActionMetadata, RawIdentity, Subscriptions } from '@/types/auth.types';

import { createDashHttpClient, type HttpRequestConfig } from '@/apis/dash.http';
import { createHttpClient } from '@/apis/http';
import { env } from '@/env';

const baseURL = env.authUrl;

const http = createDashHttpClient(baseURL);
const noAuthHttp = createDashHttpClient(baseURL, {
  includeAuth: false,
});
const simpleHttp = createHttpClient({
  baseURL,
});

export async function getSelf(config: HttpRequestConfig = {}) {
  return http.get<RawIdentity>(getGetApiSelfUrl(), {
    ...config,
  });
}

export async function validateApiKey(
  { token }: { token?: string } = {},
  config: HttpRequestConfig = {},
) {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  // Using an external axios instance here since we don't want impersonation or to logout if this fails
  return simpleHttp.get<RawIdentity>(getGetApiSelfUrl(), {
    ...config,
    headers: { ...headers, ...config.headers },
  });
}

export async function getSelfSubscriptions(
  { token }: { token?: string } = {},
  config: HttpRequestConfig = {},
) {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return http.get<Subscriptions>(getGetApiSelfSubscriptionsUrl(), {
    camelizeKeys: true,
    ...config,
    headers: { ...headers, ...config.headers },
  });
}

export async function getSelfActionMetadata(config: HttpRequestConfig = {}) {
  return http.get<ActionMetadata>(getGetApiSelfActionMetadataUrl(), {
    camelizeKeys: true,
    ...config,
  });
}

export type OAuthAuthorization = SelfOAuthAuthorization;

export async function getSelfOAuthAuthorizations(config: HttpRequestConfig = {}) {
  return http.get<OAuthAuthorization[]>(
    getGetApiSelfOauthAuthorizationsUrl(),
    merge({ camelizeKeys: true }, config),
  );
}

export async function revokeSelfOAuthAuthorization(
  { authorizationId }: { authorizationId: number },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(
    getDeleteApiSelfOauthAuthorizationsAuthorizationIdUrl(authorizationId),
    config,
  );
}

export interface CreateTokenParams {
  email?: string;
  password?: string;
  oauth?: unknown;
  otpCode?: string;
  ssoToken?: string;
}

export async function createToken(
  { email, password, oauth, otpCode, ssoToken }: CreateTokenParams,
  config: HttpRequestConfig = {},
) {
  return http.post<Token>(
    getPostApiTokensUrl(),
    {
      email,
      password,
      oauth,
      otpCode,
      ssoToken,
    },
    merge({ withCredentials: true }, config),
  );
}

export async function destroyToken(config: HttpRequestConfig = {}) {
  return http.delete<void>(getDeleteApiTokensSelfUrl(), {
    withCredentials: true,
    ...config,
  });
}

export async function getUserDetails(
  { userId }: { userId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<UserExpanded>(getGetApiUsersUserIdUrl(userId), config);
}

export async function updateUser(
  { userId, data }: { userId: number; data: Partial<UserExpandedCamel> },
  config: HttpRequestConfig = {},
) {
  return http.patch<UserExpanded>(getPatchApiUsersUserIdUrl(userId), data, config);
}

export async function resetUserPassword(
  { userId, hash, password }: { userId: number; hash: string; password: string },
  config: HttpRequestConfig = {},
) {
  return noAuthHttp.post<void>(
    getPostApiUsersUserIdResetPasswordUrl(userId),
    { hash, password },
    config,
  );
}

export async function changeUserPassword(
  { userId, password, oldPassword }: { userId: number; password: string; oldPassword: string },
  config: HttpRequestConfig = {},
) {
  return http.post<void>(
    getPostApiUsersUserIdChangePasswordUrl(userId),
    { password, oldPassword },
    merge({ camelizeKeys: true }, config),
  );
}

export async function getUsers(
  { params }: { params: Partial<GetApiUsersParams> },
  config: HttpRequestConfig = {},
) {
  return http.get<UserCompact[]>(getGetApiUsersUrl(), { ...config, params });
}

export async function getBrands(
  { params }: { params: Partial<GetApiBrandsParams> },
  config: HttpRequestConfig = {},
) {
  return noAuthHttp.get<
    (
      | BrandCompact
      | BrandCompactWithTrialPermissions
      | BrandExpanded
      | BrandExpandedWithTrialPermissions
    )[]
  >('/api/brands', merge({ params }, config));
}

export async function getBrandIntegrations(
  { brandId }: { brandId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<IntegrationDetails>(
    getGetApiBrandsBrandIdIntegrationsUrl(brandId),
    merge({ camelizeKeys: true }, config),
  );
}

export async function getUserIntegrations(config: HttpRequestConfig = {}) {
  return http.get<SelfIntegrationsResponse>(
    getGetApiSelfIntegrationsUrl(),
    merge({ camelizeKeys: true }, config),
  );
}

export async function postOAuth(
  { provider, redirectUri, brandId }: { provider: string; redirectUri: string; brandId: number },
  config: HttpRequestConfig = {},
) {
  const providerFormatted = ['pinterest_v5', 'twitter_oauth2'].includes(provider)
    ? provider
    : snakeCase(provider);
  return http.post(
    getPostApiProviderOauthUrl(providerFormatted),
    {
      redirectUri,
      brandId,
    },
    config,
  );
}

export async function getBrandUsers(
  {
    brandId,
    limit,
    offset = 0,
    email,
    search,
    sort,
    includeRestricted,
    permission,
  }: { brandId: number; permission?: string } & Partial<GetApiBrandsBrandIdUsersParams>,
  config: HttpRequestConfig = {},
) {
  const params: Record<string, unknown> = {
    limit,
    offset,
    includeRestricted,
    permission,
  };
  if (email) {
    params.email = email;
  }
  if (search) {
    params.search = search;
  }
  if (sort) {
    params.sort = sort;
  }
  return http.get<ListBrandUsers>(
    getGetApiBrandsBrandIdUsersUrl(brandId),
    merge(
      { camelizeKeys: true },
      {
        params,
      },
      config,
    ),
  );
}

export async function createBrandUser(
  {
    brandId,
    email,
    permissions,
    role,
  }: {
    brandId: number;
    email: string;
    permissions: AddNewBrandUserRequest['permissions'];
    role: string;
  },
  config: HttpRequestConfig = {},
) {
  return http.post<BrandUser>(
    getPostApiBrandsBrandIdUsersUrl(brandId),
    {
      email,
      permissions,
      role,
    },
    config,
  );
}

export async function getBrandUser(
  { brandId, userId }: { brandId: number; userId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<BrandUserExpanded>(getGetApiBrandsBrandIdUsersUserIdUrl(brandId, userId), config);
}

export async function updateBrandUser(
  {
    brandId,
    userId,
    role,
    permissions,
  }: {
    brandId: number;
    userId: number;
    role: string;
    permissions: UpdateBrandUserRequest['permissions'];
  },
  config: HttpRequestConfig = {},
) {
  return http.patch<BrandUserExpanded>(
    getPatchApiBrandsBrandIdUsersUserIdUrl(brandId, userId),
    {
      role,
      permissions,
    },
    config,
  );
}

export async function removeBrandUser(
  { brandId, userId }: { brandId: number; userId: number },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(getDeleteApiBrandsBrandIdUsersUserIdUrl(brandId, userId), config);
}

export async function resendBrandUserInvitation(
  { brandId, userId }: { brandId: number; userId: number },
  config: HttpRequestConfig = {},
) {
  return http.post<void>(
    getPostApiBrandsBrandIdUsersUserIdResendInvitationUrl(brandId, userId),
    null,
    config,
  );
}

export async function getBrandUserExportUserList(
  { userId }: { userId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<TaskResponse>(getGetApiBrandsUserUserIdExportsUserListUrl(userId), config);
}

export async function postSamlAssertion(
  { samlRequest, samlRelayState }: { samlRequest: string; samlRelayState: string },
  config: HttpRequestConfig = {},
) {
  return http.post(
    getPostApiSamlAssertionsUrl(),
    {
      samlRequest,
      relayState: samlRelayState,
    },
    config,
  );
}

export async function generateApiKey(
  { brandId, label }: { brandId: number } & CreateApiKeyRequest,
  config: HttpRequestConfig = {},
) {
  return http.post<CreateApiKeyResponse>(
    getPostApiBrandsBrandIdApiKeysUrl(brandId),
    { label },
    config,
  );
}

export async function getApiKeysForBrand(
  { brandId }: { brandId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<ListApiKeysResponse[]>(
    getGetApiBrandsBrandIdApiKeysUrl(brandId),
    merge({ camelizeKeys: true }, config),
  );
}

export async function deleteApiKeyForBrand(
  { brandId, apiKeyId }: { brandId: number; apiKeyId: number },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(getDeleteApiBrandsBrandIdApiKeysApiKeyIdUrl(brandId, apiKeyId), config);
}

export async function generatePasswordReset(
  { email }: { email: string },
  config: HttpRequestConfig = {},
) {
  return http.post<CreatePasswordResetResponse | UserExpanded>(
    getPostApiPasswordResetsUrl(),
    { email },
    config,
  );
}

export async function getPasswordResetStatus(
  { hash, userId }: { hash: string; userId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<PasswordResetsResponse>(
    getGetApiPasswordResetsUserHashUrl(hash),
    merge({ params: { userId } }, config),
  );
}

export interface GetPlatformConnectionsParams {
  brandIds?: number | number[] | string;
  brandId?: number;
  brandToken?: string;
  platformType?: string;
}

export async function getPlatformConnections(
  { brandIds, brandId, brandToken, platformType }: GetPlatformConnectionsParams,
  config: HttpRequestConfig = {},
) {
  const params: Record<string, unknown> = {};
  if (brandId) {
    params.brandId = brandId;
    params.brandToken = brandToken;
    params.platformType = platformType;
  } else if (brandIds) {
    params.brandIds = Array.isArray(brandIds) ? brandIds.join(',') : brandIds;
  }
  const axiosInstance = brandToken ? noAuthHttp : http;
  return axiosInstance.get<PublicPlatformConnection[]>(
    `/api/platform_connections`,
    merge({ params }, config),
  );
}

export interface CreatePlatformConnectionParams {
  brandId: number;
  oauth?: unknown;
  brandToken?: string;
  platformType: string;
  invitedBy?: string;
  sourceCreatorId?: number;
  creatorHandleName?: string;
  page?: unknown;
}

export async function createPlatformConnection(
  {
    brandId,
    oauth,
    brandToken,
    platformType,
    invitedBy,
    sourceCreatorId,
    creatorHandleName,
    page,
  }: CreatePlatformConnectionParams,
  config: HttpRequestConfig = {},
) {
  const axiosInstance = brandToken ? noAuthHttp : http;
  return axiosInstance.post<PublicPlatformConnection>(
    `/api/platform_connections`,
    {
      brandId,
      oauth,
      brandToken,
      platformType,
      invitedBy,
      page,
      // Used in TikTok creator OAuth flow
      sourceCreatorId,
      creatorHandleName,
    },
    config,
  );
}

export interface UpdatePlatformConnectionParams {
  connectionId: number;
  brandId: number;
  oauth?: unknown;
  brandToken?: string;
  platformType: string;
  invitedBy?: string;
}

export async function updatePlatformConnection(
  {
    connectionId,
    brandId,
    oauth,
    brandToken,
    platformType,
    invitedBy,
  }: UpdatePlatformConnectionParams,
  config: HttpRequestConfig = {},
) {
  const axiosInstance = brandToken ? noAuthHttp : http;
  return axiosInstance.patch<PublicPlatformConnection>(
    `/api/platform_connections/${connectionId}`,
    { brandId, oauth, brandToken, platformType, invitedBy },
    config,
  );
}

export async function deletePlatformConnection(
  { connectionId }: { connectionId: number },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(getDeleteApiPlatformConnectionsConnectionIdUrl(connectionId), config);
}

export interface GetAccountsParams {
  brandId: number;
  oauth?: unknown;
  brandToken?: string;
  platformType: string;
  invitedBy?: string;
}

export async function getAccounts(
  { brandId, oauth, brandToken, platformType, invitedBy }: GetAccountsParams,
  config: HttpRequestConfig = {},
) {
  const axiosInstance = brandToken ? noAuthHttp : http;
  return axiosInstance.post<
    | FacebookAvailableAccount[]
    | FacebookAnalyticsAvailableAccount[]
    | LinkedInAvailableAccountsResponse
    | SnapchatAvailableAccountsResponse
  >(
    `/api/platform_connections/accounts`,
    { brandId, oauth, brandToken, platformType, invitedBy },
    merge({ camelizeKeys: true }, config),
  );
}

export async function createBillingCheckoutSession(
  {
    brandId,
    priceLookupKey,
    redirectDomain,
  }: {
    brandId: number;
    priceLookupKey: string;
    redirectDomain: string;
  },
  config: HttpRequestConfig = {},
) {
  return http.post<StripeCheckoutSession>(
    getPostApiBillingCheckoutSessionsUrl(),
    { brandId, priceLookupKey, redirectDomain },
    merge({ camelizeKeys: true }, config),
  );
}

export async function setBrandTrialDates(
  { brandId, dhProductSubscriptionId }: { brandId: number; dhProductSubscriptionId: number },
  config: HttpRequestConfig = {},
) {
  return http.post<BrandPlanTrialDates>(
    getPostApiBrandsBrandIdTrialUrl(brandId),
    { dhProductSubscriptionId },
    merge({ camelizeKeys: true }, config),
  );
}

export async function setActionMetadata(
  {
    brandId,
    actionContext,
    payload,
  }: {
    brandId: number;
    // Derived from the builder so the two contexts the spec allows stay in one place.
    actionContext: Parameters<typeof getPatchApiBrandsBrandIdActionMetadataActionContextUrl>[1];
    payload: Record<string, unknown>;
  },
  config: HttpRequestConfig = {},
) {
  return http.patch<GeneratedActionMetadata>(
    getPatchApiBrandsBrandIdActionMetadataActionContextUrl(brandId, actionContext),
    payload,
    merge({ camelizeKeys: false }, config),
  );
}

export async function getBrandTags(
  { organizationId }: { organizationId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<BrandTagDisplay[]>(
    getGetApiOrganizationsOrganizationIdBrandTagsUrl(organizationId),
    merge({ camelizeKeys: true }, config),
  );
}

export async function createBrandTag(
  { organizationId, name, color }: { organizationId: number; name: string; color: string },
  config: HttpRequestConfig = {},
) {
  return http.post<BrandTagDisplay>(
    getPostApiOrganizationsOrganizationIdBrandTagsUrl(organizationId),
    { name, color },
    merge({ camelizeKeys: true }, config),
  );
}

export async function createBrandTagAssociations(
  {
    organizationId,
    brandIds,
    brandTagIds,
  }: {
    organizationId: number;
    brandIds: number[];
    brandTagIds: number[];
  },
  config: HttpRequestConfig = {},
) {
  const requests: Promise<unknown>[] = [];
  const chunkSize = 10;
  for (let i = 0; i < brandIds.length; i += chunkSize) {
    const brandIdsChunk = brandIds.slice(i, i + chunkSize);
    requests.push(
      http.post(
        getPostApiOrganizationsOrganizationIdBrandAssociationsUrl(organizationId),
        { brandIds: brandIdsChunk, brandTagIds },
        merge({ camelizeKeys: true }, config),
      ),
    );
  }
  await Promise.all(requests);
  return true;
}

export async function removeTagFromBrand(
  {
    organizationId,
    brandId,
    brandTagId,
  }: {
    organizationId: number;
    brandId: number;
    brandTagId: number;
  },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(
    // This route alone types its brand and tag ids as strings in the spec; its siblings use
    // integers. The template literal stringified them anyway, so the wire is unchanged.
    getDeleteApiOrganizationsOrganizationIdBrandsBrandIdTagsBrandTagIdUrl(
      organizationId,
      String(brandId),
      String(brandTagId),
    ),
    config,
  );
}

export async function deleteBrandTag(
  { organizationId, brandTagId }: { organizationId: number; brandTagId: number },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(
    getDeleteApiOrganizationsOrganizationIdBrandTagsBrandTagIdUrl(organizationId, brandTagId),
    config,
  );
}

export async function updateSubscriptionItem(
  {
    organizationId,
    priceLookupKey,
    subscriptionId = null,
  }: {
    organizationId: number;
    priceLookupKey: string;
    subscriptionId?: number | null;
  },
  config: HttpRequestConfig = {},
) {
  const data: { priceLookupKey: string; subscriptionId?: number | null } = { priceLookupKey };
  if (subscriptionId) {
    data.subscriptionId = subscriptionId;
  }
  return http.post<void>(
    getPostApiOrganizationsOrganizationIdSubscriptionItemsUrl(organizationId),
    data,
    merge({ camelizeKeys: true }, config),
  );
}

export async function getOrganizationSubscriptions(
  {
    organizationId,
    params = { brandId: null },
  }: {
    organizationId: number;
    params?: GetApiOrganizationsOrganizationIdDhProductSubscriptionsParams;
  },
  config: HttpRequestConfig = {},
) {
  return http.get<DHProductSubscription[]>(
    getGetApiOrganizationsOrganizationIdDhProductSubscriptionsUrl(organizationId),
    merge({ camelizeKeys: true }, { params }, config),
  );
}

export async function getSubscriptionSourceData(
  { organizationId, sourceIds }: { organizationId: number; sourceIds: number[] },
  config: HttpRequestConfig = {},
) {
  return http.get<SubscriptionSourceData[]>(
    `/api/organizations/${organizationId}/subscription_source_data`,
    merge({ camelizeKeys: true }, { params: { sourceIds: sourceIds.join(',') } }, config),
  );
}

export async function getCompanyCustomerInformation(
  companyId: number,
  config: HttpRequestConfig = {},
) {
  return http.get<GetCompanyCustomerInfo>(
    getGetApiCompaniesCompanyIdCustomerInformationUrl(companyId),
    merge({ camelizeKeys: true }, config),
  );
}

export async function createTrialSubscription(
  {
    organizationId = null,
    brandId = null,
    dhProductLabel,
  }: {
    organizationId?: number | null;
    brandId?: number | null;
    dhProductLabel: string;
  },
  config: HttpRequestConfig = {},
) {
  return http.post<DHProductSubscription>(
    getPostApiDhProductSubscriptionsStartTrialUrl(),
    { brandId, organizationId, dhProductLabel },
    merge({ camelizeKeys: true }, config),
  );
}

export async function getHubspotSupportToken(config: HttpRequestConfig = {}) {
  return http.post<HubspotVisitorTokenResponse>(
    getPostApiSelfHubspotVisitorTokenUrl(),
    null,
    merge({ camelizeKeys: true }, config),
  );
}

export async function getBusinessHours(
  { brandIds }: { brandIds: number[] | string },
  config: HttpRequestConfig = {},
) {
  const params = { brandIds: Array.isArray(brandIds) ? brandIds.join(',') : brandIds };
  return http.get<BusinessHoursResponse[]>(
    '/api/business_hours',
    merge({ params, camelizeKeys: true }, config),
  );
}

export async function createBusinessHours(
  { brandIds, hoursConfig, timezone }: UpsertBusinessHoursRequest,
  config: HttpRequestConfig = {},
) {
  return http.post<BusinessHoursResponse[]>(
    getPostApiBusinessHoursUrl(),
    { brandIds, hoursConfig, timezone },
    merge({ camelizeKeys: true }, config),
  );
}

export async function updateBusinessHours(
  { brandIds, hoursConfig, timezone }: UpsertBusinessHoursRequest,
  config: HttpRequestConfig = {},
) {
  return http.put<BusinessHoursResponse[]>(
    getPutApiBusinessHoursUrl(),
    { brandIds, hoursConfig, timezone },
    merge({ camelizeKeys: true }, config),
  );
}
